import { logError } from '@core/logging'
import { safeLogVariable } from '@core/logging/helpers'
import type { Push } from '@core/phoenix/core/push'

class PushError extends Error {
  constructor(readonly errorPayload: Record<string, string>) {
    super(errorPayload['reason'] ?? errorPayload.toString())
  }
}

type PushEventSuccessPayload<T> =
  T extends Push<infer M, infer K> ? M[K]['success_payload'] : never
/**
 * Converts a push into promise. It resolves with the success response or
 * rejects with the error response reason.
 *
 * @example
 *
 * ```ts
 * const push = channel.push('send_message', {})
 * promisifyPush(push).then((response) => {
 *   // Handle success response
 * }).catch((error) => {
 *   // Handle push error
 * })
 * ```
 */
export function promisifyPush<T extends Push>(push: T) {
  return new Promise<PushEventSuccessPayload<T>>((resolve, reject) => {
    push
      .receive('ok', (response) => {
        resolve(response as PushEventSuccessPayload<T>)
      })
      .receive('error', (response) => {
        logError(new Error(safeLogVariable(response)))
        reject(new PushError(response))
      })
      .receive('timeout', () => {
        logError(new Error(`Socket timeout on ${push.name}`))
        reject(new PushError({ reason: 'REASON_TIMEOUT' }))
      })
  })
}
